import { useState } from 'react';

import DnDGrid from 'components/DnDGrid';
import { useAtomValue } from 'jotai';
import dayjs from 'dayjs';
import { insertItemInToDisplay } from 'actions/share';
import { displayPreviewAtom, openSharePopup as openSharePopupAtom, setDisplayPreview as setDisplayPreviewJotai } from '@src/state/shareAtoms';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';
import * as appHelpers from '@src/services/helpers/appHelpers';

import { api } from '@src/services/api';
import { ShareCollectionContentOverlay } from 'components/ShareCollectionContentOverlay';
import { DisplayPreviewContentView } from 'common';
import { SharePopupInfo, DisplayInsertInfo } from '@src/types/share';
import { WarningIcon } from '@src/components/WarningIcon';
import { MediaType } from '@adalong/schemas';
import Tooltip from '@mui/material/Tooltip';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { stylesheet } from 'typestyle';
import { Column } from '@src/components/Utils';
import { Hide } from '@src/components/Hide';
import translation from './translation';
import styles from './styles.module.scss';

const lang = appHelpers.loadTranslation(translation);

interface Props {
  incorrectContentOrder: { [key: string]: boolean }
}

export function GridWrapper({ incorrectContentOrder }: Props) {
  const displayPreview = useAtomValue(displayPreviewAtom);
  const [unavailable, setUnavailable] = useState<{ [key: string]: boolean | undefined }>({});

  function getContentInfo(content: DisplayPreviewContentView) {
    const info: SharePopupInfo = {
      image: '',
      file: '',
      fileType: content.media?.type && content.media.type != MediaType.Text && content.media.type != MediaType.Untyped ? content.media.type : MediaType.Image,
      isInstaVideo: content.media?.source.name === 'instagram' && content.media.type === MediaType.Video,
      comment: content.comment,
      _id: content._id,
    };
    if (content.date) {
      info.date = content.date.toString();
    }
    if (content.brandcontent) {
      info.image = content.brandcontent.urls.thumbnail;
      info.file = content.brandcontent.urls.file;
      info.fileType = content.brandcontent.type;
    } else if (content.media) {
      info.image = content.media.cdn_image;
      info.fileType = content.media.type === MediaType.Image ? 'image' : 'video';
      info.creator = content.media.source.user_name;
    }
    return info;
  }

  function onShare(content: DisplayPreviewContentView) {
    const info = getContentInfo(content);
    openSharePopupAtom('displaypreview', {
      ...info,
      displayContentId: content._id,
      saveDraft: handleSaveDraft(content),
    });
  }

  function handleSaveDraft(content: DisplayPreviewContentView) {
    return async function (comment?: string, date?: Date) {
      return api.saveDraftInItemFromDisplay(content._id, comment, date).then((dp) => {
        setDisplayPreviewJotai(dp);
        return true;
      }).catch(() => {
        appHelpers.addAlert({ text: lang.error.unknown, type: 'danger' });
        return false;
      });
    };
  }

  function onInsert(info: DisplayInsertInfo, position: number) {
    if (!displayPreview?.content) {
      return;
    }

    const updated = { ...displayPreview };
    const content: DisplayPreviewContentView = {
      type: info.type,
      [info.type]: info.content,
    } as any;
    updated.content!.splice(position, 0, content);
    setDisplayPreviewJotai(updated);

    insertItemInToDisplay(info, position).catch((e) => {
      console.error(e);
      appHelpers.addAlert({ text: lang.error.insert, type: 'danger' });
    });
  }

  function onMoveItem(itemIndex: number, newPosition: number) {
    if (!displayPreview?.content) {
      return;
    }

    const originalDisplayPreview = structuredClone(displayPreview);

    const updated = { ...displayPreview };
    const content = [...updated.content!];
    const [item] = content.splice(itemIndex, 1);
    if (!item._id) {
      return;
    }
    content.splice(newPosition, 0, item);
    updated.content = content;
    setDisplayPreviewJotai(updated);

    api.moveItemInDisplay(item._id, newPosition).catch((e) => {
      console.error(e);
      setDisplayPreviewJotai(originalDisplayPreview);
      appHelpers.addAlert({ text: lang.error.unknown, type: 'danger' });
    });
  }

  function onRemoveItem(itemIndex: number) {
    if (!displayPreview?.content) {
      return;
    }

    const originalDisplayPreview = structuredClone(displayPreview);

    const updated = { ...displayPreview };
    const content = [...updated.content!];
    const [item] = content.splice(itemIndex, 1);
    if (!item._id) {
      return;
    }
    updated.content = content;
    setDisplayPreviewJotai(updated);

    api.removeItemFromDisplay(item._id).catch((e) => {
      console.error(e);
      setDisplayPreviewJotai(originalDisplayPreview);
      appHelpers.addAlert({ text: lang.error.unknown, type: 'danger' });
    });
  }

  function handleImageError(content: DisplayPreviewContentView) {
    setUnavailable((prev) => ({ ...prev, [content._id]: true }));
  }

  function validInsertInfo(info: any) {
    if (!info || !['share'].includes(info.origin)) {
      return false;
    }
    return true;
  }

  function renderContent() {
    if (!displayPreview?.content) {
      return null;
    }
    return displayPreview.content.map((content, i) => {
      const url = getContentInfo(content).image;
      const available = !unavailable[content._id];
      return (
        <div key={i} className={styles.item}>
          <div className={styles.imageContainer}>
            <div
              className={styles.image}
              onError={() => handleImageError(content)}
            >
              <Hide hide={!available}>
                <Column className={sheet.imageContainer}>
                  <img className={sheet.image} src={url} />
                </Column>
              </Hide>
              {incorrectContentOrder[content._id] ? (
                <div className={styles.warning}>
                  <WarningIcon className={styles.warningIcon} />
                </div>
              ) : false}
              {available ? (
                <ShareCollectionContentOverlay
                  onShare={() => onShare(content)}
                  isPreview={true}
                >
                  {content.date ? (
                    <div className={styles.date}>
                      <ScheduleIcon />
                      <span>
                        {dayjs(content.date).format('MMM. D h:mma')}
                      </span>
                    </div>
                  ) : null}
                  <DeleteIcon className={styles.delete} onClick={() => onRemoveItem(i)} />
                </ShareCollectionContentOverlay>
              ) : (
                <>
                  <Tooltip title={texts.deadURL}>
                    <span>
                      <WarningIcon className={styles.warningIcon} />
                    </span>
                  </Tooltip>
                  <DeleteIcon className={styles.delete} onClick={() => onRemoveItem(i)} />
                </>
              )}
            </div>
          </div>
        </div>
      );
    });
  }

  return (
    <div className={styles.grid}>
      <DnDGrid
        onInsert={onInsert}
        onMoveItem={onMoveItem}
        validInsertInfo={validInsertInfo}
        gridElements={renderContent()}
      >
        {renderContent()}
      </DnDGrid>
    </div>
  );
}

const texts = LocalizedStrings({
  en: {
    deadURL: 'Image not available',
  },
});

const sheet = stylesheet({
  imageContainer: {
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
  },
  image: {
    margin: 'auto',
    height: '100%',
  },
});

export default GridWrapper;
